import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";
import { SaleAction } from "actions";

import { DeploymentSaleCommentAction } from "actions/comments";

import { Clickable } from "components/Button";
import { Row } from "components/Layout";
import { SaleSearch } from "components/SearchInput/GlobalSearch";
import { BigText } from "components/Text";

import { CommentType } from "constants/comments";
import { Settings } from "constants/settings";
import { userTypes } from "constants/users";

import { getSetting, selectIsUserOfType } from "selectors";

import { useIsMobile } from "hooks";
import { LoadAll } from "components/AgGrid/LoadAll";
import { ViewSelector, viewTypes } from "containers/UserDashboard/ViewSelector";
import { GridUserDashboard } from "containers/UserDashboard/Grid";
import { CardUserDashboard } from "containers/UserDashboard/Cards";
import { AddSaleInEmptyState } from "components/Button/AddSaleButton";

const MainWrapper = styled.div(
  ({ theme }) => `
  padding-top: ${theme.space[3]}px;
  display: flex;
  flex-direction: column;
  height: 100%;
  @media (max-width: ${theme.breakpoints[0]}px) {
    margin: 0;
  }  
`,
);

const LoadMoreButton = styled(Clickable)`
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.primary};
  position: relative;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex: 1;
  padding-left: 12px;
  padding-right: 12px;
  align-items: center;
`;

const TitleContainer = styled(HeaderContainer)`
  padding-left: 0;
`;

interface LoadMoreSalesButtonProps {
  loadMoreSales: () => void;
}
const LoadMoreSalesButton = ({
  loadMoreSales,
}: LoadMoreSalesButtonProps): React.JSX.Element => {
  const allSalesLoaded = useSelector(getSetting(Settings.allSalesLoaded));

  const isMobile = useIsMobile();

  return (
    <LoadAll
      allLoaded={allSalesLoaded}
      onClick={loadMoreSales}
      text="Last 90 days loaded."
      fullWidth={isMobile}
    />
  );
};

interface CardViewHeaderProps {
  canCreateSales: boolean;
  loadMoreSales: () => void;
}

const CardViewHeader = ({
  canCreateSales,
  loadMoreSales,
}: CardViewHeaderProps): React.JSX.Element => {
  const isMobile = useIsMobile();
  const allSalesLoaded = useSelector(getSetting(Settings.allSalesLoaded));

  const saleSearchFooterTextAndAction = (
    <>
      This filter is only applying to sales currently on screen. To see more
      results, first{" "}
      <LoadMoreButton data-tour="loadMoreSales" onClick={loadMoreSales}>
        Load more sales
      </LoadMoreButton>
    </>
  );

  return (
    <>
      <Row
        fullWidth={isMobile}
        minWidth={!isMobile ? "400px" : undefined}
        marginHorizontal={1}
      >
        <SaleSearch
          overridePlaceHolder="Filter..."
          showFooter={!allSalesLoaded}
          footerTextAndAction={saleSearchFooterTextAndAction}
        />
      </Row>

      {!isMobile && (
        <AddSaleInEmptyState
          buttonText="Add Sale"
          canCreateSale={canCreateSales}
          useStyledButton
        />
      )}
    </>
  );
};

export const UserDashboard = () => {
  const isMobile = useIsMobile();
  const allSalesLoaded = useSelector(getSetting(Settings.allSalesLoaded));
  const canCreateSales = useSelector(
    selectIsUserOfType([
      userTypes.SCALE_OPERATOR,
      userTypes.SALEYARD_ADMIN,
      userTypes.LIVESTOCK_AGENT,
    ]),
  );

  const dashboardView =
    useSelector(getSetting(Settings.dashboardView)) || viewTypes.CARD;

  const currentView = isMobile ? viewTypes.CARD : dashboardView;

  const dispatch = useDispatch();
  const loadMoreSales = () => {
    if (!allSalesLoaded) {
      dispatch(SaleAction.requestChunked({ fetchAll: true }));
    }
  };

  useEffect(() => {
    const requestAction = DeploymentSaleCommentAction.request({
      livestockSaleSensitive: false,
      saleyardSensitive: false,
      commentType: CommentType.DEPLOYMENT_SALE,
    });
    dispatch(requestAction);
  }, [dispatch]);

  return (
    <MainWrapper>
      <Row
        fullWidth
        justifyBetween
        alignCenter
        marginVertical={2}
        paddingHorizontal={3}
        flexWrap
      >
        <TitleContainer>
          <Row
            fullWidth
            justifyBetween={isMobile}
            justifyStart={!isMobile}
            alignCenter
            padding={1}
          >
            <BigText>Sales</BigText>
            {!isMobile && (
              <>
                &nbsp;
                <ViewSelector />
              </>
            )}
            {isMobile && (
              <AddSaleInEmptyState
                canCreateSale={canCreateSales}
                buttonText="Add Sale"
                useStyledButton
              />
            )}
          </Row>
        </TitleContainer>
        {isMobile ? (
          <Row fullWidth padding={1}>
            <LoadMoreSalesButton loadMoreSales={loadMoreSales} />
          </Row>
        ) : (
          <HeaderContainer>
            <LoadMoreSalesButton loadMoreSales={loadMoreSales} />
          </HeaderContainer>
        )}

        {currentView === viewTypes.CARD || !isMobile ? (
          <HeaderContainer>
            {currentView === viewTypes.CARD ? (
              <Row fullWidth justifyEnd>
                <CardViewHeader
                  canCreateSales={canCreateSales}
                  loadMoreSales={loadMoreSales}
                />
              </Row>
            ) : null}
          </HeaderContainer>
        ) : null}
      </Row>
      {currentView === viewTypes.CARD ? (
        <CardUserDashboard canCreateSales={canCreateSales} />
      ) : (
        <GridUserDashboard />
      )}
    </MainWrapper>
  );
};
